import { useEffect, useRef } from 'react';

import { useAuthService } from '@bessemercws/auth';
import { useApolloWrapperContext, useMutation, REVOKE_USER_SESSION } from '@bessemercws/graphql';
import { useClientContext } from '@bessemercws/providers';
import { useNavigate } from 'react-router-dom';


const EVENTS = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

interface AppTimeoutProps {
  children: JSX.Element;
  /**
   * number of milliseconds before user is signed out
   * @default 900,000 (15 minutes)
   */
  timeout?: number;
}

const AppTimeout = ({ children, timeout = 900000 }: AppTimeoutProps) => {
  const isDev = import.meta.env.MODE === 'dev';

  const authService = useAuthService();
  const { client } = useApolloWrapperContext();
  const navigate = useNavigate();
  const { setIdToken, userId, isAdvisor, setSelectedClient } = useClientContext();
  const [revokeUserSession] = useMutation(REVOKE_USER_SESSION);

  // Track isAdvisor changes
  const isAdvisorRef = useRef(isAdvisor);

  let timer: NodeJS.Timeout;

  function setTimer() {
    timer = setTimeout(onTimerFinish, timeout);
  }

  function clearTimer() {
    if (timer) clearTimeout(timer);
  }

  function onTimerFinish() {
    // clear any pending timers
    clearTimer();

    // Listener clean up
    EVENTS.forEach((event) => {
      window.removeEventListener(event, clearTimer);
    });

    // logout the user, if not in dev environment
    if (isDev){
      return;
    } 
    
    const hadAuthenticatedSession = sessionStorage.getItem('okta-token-storage') !== null;
    if(hadAuthenticatedSession) {
      sessionStorage.setItem('sessionExpired', JSON.stringify({ expired: true, ttl: new Date().toISOString() }));
    }

    authService.signOut(() => {
        setIdToken(undefined);
        // Call revokeUserSession mutation
        if (userId) {
          revokeUserSession({
            variables: {
              input: {
                userId,
              },
            },
          }).catch((error) => {
            console.error('Error revoking user session:', error);
          });
        }

        client.clearStore();

        if (isAdvisorRef.current) {
          setSelectedClient(undefined);
          navigate('/advisors');
        }
        else {
          navigate('/login');
        }
      });
  }

  // Monitor isAdvisor changes
  useEffect(() => {
    if (isAdvisorRef.current !== isAdvisor) {
      isAdvisorRef.current = isAdvisor;
    }
  }, [isAdvisor]);

  useEffect(() => {
    const eventHandler = () => {
      clearTimer();
      setTimer();
    };

    EVENTS.forEach((event) => {
      window.addEventListener(event, eventHandler);
    });

    return () => {
      // Clean up the event listeners on unmount
      EVENTS.forEach((event) => {
        window.removeEventListener(event, eventHandler);
      });
    }
  }, []);

  return children;
};

export default AppTimeout;
export { AppTimeout, AppTimeoutProps };
